import type { Ref } from 'vue';
import { computed, ref } from 'vue';
import type { DsrResource } from '@getaccept/lib-shared/src/library/types/dsr-resource';
import type { DsrDirectory } from '@getaccept/lib-shared/src/library/types/dsr-directory';
import { DsrResourceType } from '@getaccept/lib-shared/src/library/types/enums/dsr-resource-type';
import { DsrDirectoryConstants } from '@getaccept/lib-shared/src/library/constants/dsr-directory.constants';
import { SortOrder } from '@getaccept/fried-tofu';
import { DsrSortAttribute } from '../../api/dsr/types/enums/dsr-sort-attribute';
import { DsrService } from '../../api/dsr/services/dsr.service';
import { LibraryService } from '../../api/library/services/library.service';

export function useLoadTemplates() {
  const directories: Ref<DsrDirectory[]> = ref([]);
  const templates: Ref<DsrResource[]> = ref([]);
  const currentDirectory = ref(DsrDirectoryConstants.RootPath);
  const sortOrder = ref(SortOrder.Desc);
  const sortBy = ref(DsrSortAttribute.Title);
  const currentSearchTerm = ref('');

  const loadPayload = {
    offset: 0,
    limit: 30,
    resourceType: DsrResourceType.DsrTemplate,
  };

  const loadingLastUsed = ref(false);
  const errorLastUsed = ref(false);
  const lastUsedTemplates: Ref<DsrResource[]> = ref([]);

  const loadLastUsed = async () => {
    loadingLastUsed.value = true;
    errorLastUsed.value = false;

    try {
      const { resources } = await DsrService.loadResources({
        ...loadPayload,
        sortOrder: SortOrder.Desc,
        sortBy: DsrSortAttribute.LastUsed,
        limit: 5,
      });

      lastUsedTemplates.value = resources;
    } catch (e) {
      errorLastUsed.value = true;
    } finally {
      loadingLastUsed.value = false;
    }
  };

  const searching = ref(false);
  const searchError = ref(false);

  const searchTemplates = async (searchTerm: string, tagIds?: string[]) => {
    searching.value = true;
    searchError.value = false;
    currentSearchTerm.value = searchTerm;

    try {
      const { resources } = await DsrService.loadResources({
        ...loadPayload,
        sortOrder: sortOrder.value,
        sortBy: sortBy.value,
        searchTerms: [currentSearchTerm.value],
        tagIds,
      });
      directories.value = [];
      currentDirectory.value = DsrDirectoryConstants.RootPath;
      templates.value = resources;
    } catch (e) {
      searchError.value = true;
    } finally {
      searching.value = false;
    }
  };

  const loading = ref(false);
  const error = ref(false);
  const currentTotalCount = ref(0);

  const loadByDirectory = async (directory = DsrDirectoryConstants.RootPath) => {
    loading.value = true;
    error.value = false;
    directories.value = [];
    templates.value = [];

    try {
      const [templatesObject, directoriesObject] = await Promise.all([
        DsrService.loadResources({
          ...loadPayload,
          sortOrder: sortOrder.value,
          sortBy: sortBy.value,
          directory,
        }),
        LibraryService.loadDirectories({ offset: 0, limit: 200, directory }),
      ]);

      currentDirectory.value = directory;
      await setFolders(directoriesObject.directories);
      templates.value = [...templatesObject.resources];
      currentTotalCount.value = templatesObject.totalCount;
    } catch (e) {
      error.value = true;
    } finally {
      loading.value = false;
    }
  };

  const hasNextPage = computed(() => templates.value.length < currentTotalCount.value);

  const loadMoreResources = async (tagIds?: string[]) => {
    error.value = false;

    try {
      const { resources, totalCount } = await DsrService.loadResources({
        ...loadPayload,
        sortOrder: sortOrder.value,
        sortBy: sortBy.value,
        directory: currentDirectory.value,
        offset: templates.value.length,
        searchTerms: currentSearchTerm.value ? [currentSearchTerm.value] : undefined,
        tagIds,
      });

      templates.value = [...templates.value, ...resources];
      currentTotalCount.value = totalCount;
    } catch (e) {
      error.value = true;
    }
  };

  const setFolders = async (folders: DsrDirectory[]) => {
    error.value = false;
    try {
      const allFolders = await Promise.all(
        folders.map(async folder => {
          const { totalCount } = await DsrService.loadFolderTotalCount({
            ...loadPayload,
            directory: `${folder.directory}${folder.title}/`,
            includeSubDirectories: true,
          });

          return totalCount ? folder : null;
        })
      );

      directories.value = allFolders.filter(Boolean);
    } catch (e) {
      error.value = true;
    }
  };

  const setSort = (by: DsrSortAttribute, order: SortOrder) => {
    sortOrder.value = order;
    sortBy.value = by;
  };

  return {
    loadByDirectory,
    loadMoreResources,
    hasNextPage,
    loading,
    error,
    loadLastUsed,
    loadingLastUsed,
    lastUsedTemplates,
    searchTemplates,
    searching,
    templates,
    currentDirectory,
    directories,
    setSort,
    sortOrder,
    sortBy,
    setFolders,
  };
}
